import { ArrowToBottomIcon, PencilIcon } from 'components/icons';
import { getFeaturesModule } from 'containers/selfOnboarding/helpers/utils';
import React from 'react';
import cn from 'classnames';
import { useLocation } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Button, TabConfig, useMediaQuery } from '@odin-labs/components';
import { Dropzone } from 'components/dropzone';
import { acceptImages } from 'components/dropzone/utils';
import {
  RoutedTabsHeader,
  RoutedTabsPages,
  useRoutedTabs,
} from 'components/tabs';
import {
  ReviewCompletedProfiledTabProps,
  SelfOnboardingStepProps,
} from 'containers/selfOnboarding/steps/types';
import { Copy, getBaseRoute } from 'containers/selfOnboarding/steps/utils';
import { SearchWorkerInfoItem } from 'components/searchBox/searchWorkerInfo/SearchWorkerInfoItem';
import { getCurrentDomain } from 'utils';
import { ODIN_APP_DOWNLOAD_URL } from 'utils/constants';
import {
  StepInfo,
  StepInfoTitle,
  StepJobsiteInfo,
} from 'containers/selfOnboarding/steps/components';
import {
  ReviewCompletedProfileWorkerInfoTab,
  ReviewCompletedProfileDocumentsTab,
} from './tabs';
import { ReviewProfilePhotoContainer } from './components';

const classes = {
  workerTabPage: '',
  editPhotoIconHolder: cn(
    'odin-relative -odin-ml-6 odin-text-odin-primary',
    'odin-w-10 odin-h-10 odin-rounded-full odin-bg-white odin-shadow-md',
    'odin-flex odin-items-center odin-justify-center odin-cursor-pointer',
  ),
};

export const tabsConfig: TabConfig<ReviewCompletedProfiledTabProps>[] = [
  {
    name: 'workerInfo',
    relativePath: '',
    component: ReviewCompletedProfileWorkerInfoTab,
  },
  {
    name: 'documents',
    relativePath: '/documents',
    component: ReviewCompletedProfileDocumentsTab,
  },
];

const tabsCopies: Record<string, Copy> = {
  workerInfo: Copy.worker_info,
  documents: Copy.documents,
};

export function ReviewCompletedProfileStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    navigation,
    loading,
    jobsiteInvitation,
    stepConfig,
    localize,
  } = props;
  const { currentStep, getStepNumberInfo } = navigation;
  const { file } = state.profilePhoto ?? {};
  const { jobsiteInvitationId, jobsiteContractor } = jobsiteInvitation ?? {};
  const { jobsite } = jobsiteContractor ?? {};
  const { worker, jobsiteWorker, basicInfo, contractorId } = state;
  const { quickCode, workerId } = worker ?? {};
  const { firstName, lastName } = basicInfo ?? {};

  const isWorkerAppEnabled = !!getFeaturesModule(jobsite?.modules)
    ?.workerAppEnabled;

  const location = useLocation();
  const baseRoute = `${getBaseRoute(jobsiteInvitationId)}/${currentStep}`;
  const localizedTabsConfig = tabsConfig.map((tab) => ({
    ...tab,
    text: localize(tabsCopies[tab.name]),
  }));
  const { tabs, currentTab } = useRoutedTabs({
    tabsConfig: localizedTabsConfig,
    baseRoute,
    search: location.search,
  });

  const tabsPageProps: ReviewCompletedProfiledTabProps = React.useMemo(
    () => ({
      jobsiteInvitation,
      state,
      loading,
      navigation,
      stepConfig,
      localize,
    }),
    [jobsiteInvitation, state, loading, navigation, stepConfig, localize],
  );

  const qrUrl = `${getCurrentDomain()}/worker/${workerId}`;
  const isSubSmall = useMediaQuery('(width < 768px)');
  const avatarDisplaySize = React.useMemo(
    () => (isSubSmall ? 'sm' : 'xs'),
    [isSubSmall],
  );

  return (
    <div className="md:odin-grid md:odin-grid-cols-[1fr_2fr]">
      <StepInfo>
        <StepInfoTitle
          title={localize(Copy.self_onboarding_review_completed_profile_header)}
          subtitle={localize(
            Copy.self_onboarding_review_completed_profile_instructions,
          )}
          completeStep
          withBottomBorder
          stepNumberInfo={getStepNumberInfo()}
        />
        <StepJobsiteInfo
          jobsiteInvitation={jobsiteInvitation}
          jobsiteWorker={jobsiteWorker}
          contractorName={contractorId?.label}
        />
      </StepInfo>
      <div>
        <div className="md:odin-justify-start md:odin-flex-row md:odin-items-end md:odin-gap-2 md:odin-pl-7.5 md:odin-pr-7.5 md:odin-pt-0 sm:odin-pt-4 odin-pt-0 odin-justify-center odin-flex odin-flex-col">
          <div className="odin-flex odin-justify-center odin-min-w-[80px]">
            <ReviewProfilePhotoContainer
              navigation={navigation}
              className="odin-items-end"
            >
              <Dropzone
                name="profilePicture"
                value={file}
                isAvatar
                avatarSize={avatarDisplaySize}
                accept={acceptImages}
                hideAvatarChangePhotoButton
              />
              <div className={classes.editPhotoIconHolder}>
                <PencilIcon className="odin-h-4 odin-w-4" />
              </div>
            </ReviewProfilePhotoContainer>
            <QRCode
              value={qrUrl}
              size={120}
              className="md:odin-hidden odin-block odin-pl-2.5"
            />
          </div>
          <div className="md:odin-grid md:odin-grid-cols-[1fr_auto] odin-flex odin-justify-center odin-grow odin-min-w-0">
            <div className="odin-min-w-0 odin-px-7.5 sm:odin-px-0">
              <div className="odin-mt-3 odin-mb-1.5 odin-text-center sm:odin-text-left odin-text-gray-900 odin-text-2.66xl odin-font-bold odin-min-w-0 odin-whitespace-nowrap odin-overflow-hidden odin-text-ellipsis">
                {firstName} {lastName}
              </div>
              <SearchWorkerInfoItem type="quickCode" text={quickCode} />
            </div>
            <QRCode
              value={qrUrl}
              size={72}
              className="md:odin-block odin-hidden"
            />
          </div>
        </div>
        {isWorkerAppEnabled && (
          <div className="odin-flex odin-justify-center odin-px-7.5 odin-pt-4 lg:odin-pt-7.5">
            <Button
              as="a"
              className="odin-w-full odin-justify-center"
              size="lg"
              text={localize(Copy.download_odin_app)}
              icon={ArrowToBottomIcon}
              href={ODIN_APP_DOWNLOAD_URL}
            />
          </div>
        )}
        <div className="odin-p-7.5">
          <RoutedTabsHeader tabs={tabs} currentTab={currentTab} />
          <RoutedTabsPages
            className={classes.workerTabPage}
            baseRoute={baseRoute}
            tabs={tabs}
            componentProps={tabsPageProps}
          />
        </div>
      </div>
    </div>
  );
}
